import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useInterviewStore } from '@/store/interviewStore'
import { useInterview } from '@/hooks/useInterview'
import { AvatarVideo } from '@/components/interview/AvatarVideo'
import { ControlBar } from '@/components/interview/ControlBar'
import { InterviewSummary } from '@/components/interview/InterviewSummary'
import type { InterviewConfig } from '@/types/interview'

type Screen = 'config' | 'interview' | 'summary'

export function CompanyPage() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [screen, setScreen] = useState<Screen>('config')
  const [companyName, setCompanyName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [question1, setQuestion1] = useState('')
  const [question2, setQuestion2] = useState('')
  const [error, setError] = useState('')

  const avatarVideoRef = useRef<HTMLVideoElement>(null)
  const store = useInterviewStore()
  const interview = useInterview()

  useEffect(() => {
    if (avatarVideoRef.current) interview.videoElRef.current = avatarVideoRef.current
  })

  useEffect(() => {
    if (store.status === 'complete' && screen === 'interview') setScreen('summary')
  }, [store.status, screen])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!companyName || !jobTitle || !question1 || !question2) {
      setError('Bitte alle Felder ausfüllen.')
      return
    }
    setError('')
    const config: InterviewConfig = {
      language: 'de',
      avatarGender: 'male',
      mode: 'full',
      question1,
      question2,
    }
    try {
      await interview.setup(config)
      setScreen('interview')
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    }
  }

  function handleStart() { interview.startInterview().catch(console.error) }
  function handleNext() { interview.advanceSegment() }
  function handleEnd() { interview.endInterview().then(() => setScreen('summary')) }
  function handleLogout() { logout(); navigate('/login', { replace: true }) }

  return (
    <div className="container">
      <header className="header">
        <div className="header-content">
          <h1>🎥 TEMBO</h1>
          <p>KI-gestützte Video-Interviews mit realistischen Avataren</p>
        </div>
        <div className="logout-form">
          <button type="button" className="logout-btn" onClick={handleLogout}>🚪 Abmelden</button>
        </div>
      </header>

      {screen === 'config' && (
        <div id="config-form" className="config-form">
          <h3>Konfiguriere nun dein Interview</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="company-name">Firmenname:</label>
              <input id="company-name" type="text" placeholder="z.B. TechCorp GmbH" value={companyName} onChange={e => setCompanyName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="job-title">Stellenbezeichnung:</label>
              <input id="job-title" type="text" placeholder="z.B. Senior Softwareentwickler" value={jobTitle} onChange={e => setJobTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Interview-Fragen:</label>
              <p style={{ color: '#666', marginBottom: 15 }}>Definiere 2 Interview-Fragen.</p>
              <div className="form-group" style={{ marginBottom: 15 }}>
                <label htmlFor="question1"><span className="question-number">1</span>Erste Frage:</label>
                <textarea id="question1" placeholder="Frage nach Hintergrund..." value={question1} onChange={e => setQuestion1(e.target.value)} required />
              </div>
              <div className="form-group">
                <label htmlFor="question2"><span className="question-number">2</span>Zweite Frage:</label>
                <textarea id="question2" placeholder="Frage nach einem Projekt..." value={question2} onChange={e => setQuestion2(e.target.value)} required />
              </div>
            </div>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            <div style={{ textAlign: 'center', marginTop: 30 }}>
              <button type="submit" className="btn btn-primary" disabled={store.status === 'setup'}>
                {store.status === 'setup' ? 'Verbindet...' : 'Vollständiges Interview starten'}
              </button>
            </div>
          </form>
        </div>
      )}

      {screen === 'interview' && (
        <div id="interview-interface" className="interview-interface">
          <div className="interview-style-container">
            <div className="main-video-area">
              <AvatarVideo ref={avatarVideoRef} avatarEnabled={store.avatarEnabled} isConnecting={store.status === 'setup'} />
              <div className="self-video-container">
                <video autoPlay muted playsInline
                  ref={(el) => { if (el && interview.streamRef.current) el.srcObject = interview.streamRef.current }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div className="self-video-placeholder">
                  <div className="self-avatar-circle"><div className="self-avatar-icon">👤</div></div>
                </div>
              </div>
            </div>
            <ControlBar
              status={store.status}
              phase={store.currentPhase}
              agentMessage={store.agentMessage}
              realtimeTranscript={store.realtimeTranscript}
              recordingElapsed={store.recordingElapsed}
              onStart={handleStart}
              onNext={handleNext}
              onEnd={handleEnd}
              startLabel="Interview starten"
              nextLabel="Weiter"
              endLabel="Beenden"
            />
          </div>
        </div>
      )}

      {screen === 'summary' && (
        <InterviewSummary language="de" transcripts={store.transcripts} />
      )}
    </div>
  )
}
